// =============================================================================
// components/create/CreateHub.tsx — the FULL-SCREEN Instagram-style create
// surface (replaces the old compact CreateChooser bottom sheet).
//
// Slid in by CreateSwipeGesture's right-edge left-swipe (the pan drives an
// overlay <Animated.View> hosting this component — see CreateSwipeGesture.tsx).
// This is a complete screen, not a sheet:
//
//   • Top bar   — glass close (X) pill + "Create" title.
//   • Centre    — one BIG per-mode preview/entry card (gradient icon medallion,
//                 title, description, "Tap to start" CTA). Tapping it routes
//                 into the REAL flow for the selected mode.
//   • Bottom    — the hero MODE SWITCHER: POST · STORY · ARTICLE, an
//                 Instagram-style horizontal glass segmented pill with a
//                 spring-slid primary indicator.
//
// Modes → registered RootStack routes (no dead buttons):
//   POST    → CreatePost modal
//   STORY   → StoryCreate fullScreenModal
//   ARTICLE → CreatePost with { type:'article' } (preselects Article mode)
//
// Glass is NEVER re-authored here — surfaces are GlassBar/GlassSurface and all
// values come from glassTokens / theme. Background = app lavender + blobs so
// the glass reads correctly full-screen.
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { GlassBar, GlassSurface, glassTokens } from '../glass';
import BackgroundBlobs from '../ui/BackgroundBlobs';
import { Ionicons, IoniconName } from '../ui/Icon';
import { colors, fonts, spacing } from '../../constants/theme';
import type { RootStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

export interface CreateHubProps {
  /** Animated dismissal (X button) — slides the hub back off-screen. */
  onClose: () => void;
  /**
   * Called right after a mode routes into its real flow, so the host can reset
   * the overlay instantly behind the incoming modal (no slide-out flash).
   */
  onLaunched: () => void;
}

// The three real create destinations. Each maps to a registered RootStack
// screen — the switcher never offers a dead mode.
type Mode = {
  key: 'post' | 'story' | 'article';
  label: string;
  icon: IoniconName;
  title: string;
  desc: string;
  cta: string;
  go: (nav: Nav) => void;
};

const MODES: Mode[] = [
  {
    key: 'post',
    label: 'POST',
    icon: 'chatbubble-ellipses',
    title: 'New Post',
    desc: 'Share what’s happening — a short update, up to 280 characters.',
    cta: 'Start a post',
    go: (nav) => nav.navigate('CreatePost'),
  },
  {
    key: 'story',
    label: 'STORY',
    icon: 'camera',
    title: 'New Story',
    desc: 'A photo or video moment for your followers — gone in 24 hours.',
    cta: 'Open the camera',
    go: (nav) => nav.navigate('StoryCreate'),
  },
  {
    key: 'article',
    label: 'ARTICLE',
    icon: 'document-text',
    title: 'New Article',
    desc: 'Long-form writing — analysis, guides and deep dives.',
    cta: 'Start writing',
    go: (nav) => nav.navigate('CreatePost', { type: 'article' }),
  },
];

const SPRING = { damping: 24, stiffness: 300 } as const;

export default function CreateHub({ onClose, onLaunched }: CreateHubProps) {
  // Inside the Tabs tree; navigate() bubbles to the RootStack where the
  // CreatePost / StoryCreate modals live.
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();

  const [modeIndex, setModeIndex] = useState(0);
  const mode = MODES[modeIndex];

  // ---- Mode switcher: sliding indicator ------------------------------------
  // Measured track width → segment width; the primary indicator springs to the
  // active segment (Instagram's mode-carousel feel, adapted to a glass pill).
  const [trackWidth, setTrackWidth] = useState(0);
  const segmentWidth = trackWidth > 0 ? trackWidth / MODES.length : 0;
  const indicatorX = useSharedValue(0);

  useEffect(() => {
    indicatorX.value = withSpring(modeIndex * segmentWidth, SPRING);
  }, [modeIndex, segmentWidth, indicatorX]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: indicatorX.value }],
  }));

  // ---- Launch the real flow -------------------------------------------------
  const launch = useCallback(
    (m: Mode) => {
      m.go(navigation);
      // Let the incoming modal cover the hub, then reset the overlay silently
      // so Home is clean when the user comes back.
      setTimeout(onLaunched, 450);
    },
    [navigation, onLaunched],
  );

  return (
    <View style={styles.root}>
      <BackgroundBlobs />

      {/* ---- Top bar: close (X) + title ---- */}
      <View style={[styles.topBar, { paddingTop: insets.top + spacing.sm }]}>
        <GlassSurface
          variant="pill"
          radius={glassTokens.radius.pill}
          onPress={onClose}
          accessibilityLabel="Close create"
          accessibilityRole="button"
          style={styles.closeHost}
          contentStyle={styles.closeContent}
        >
          <Ionicons name="close" size={24} color={colors.ink} />
        </GlassSurface>
        <Text style={styles.title}>Create</Text>
        {/* spacer balances the X so the title stays optically centred */}
        <View style={styles.closeSpacer} />
      </View>

      {/* ---- Big per-mode preview/entry area ---- */}
      <View style={styles.stage}>
        {/* key={mode.key} remounts on mode change. NOTE: no reanimated layout
            `entering` animation here — on RN 0.85 bridgeless New Arch, entering/
            layout animations abort at mount (worklets scheduleOnUI→runSync SIGABRT,
            crashing the app ~0.7s after the authed tree mounts). This was THE
            post-login crash. Mode swap is instant; re-add a fade later via the
            imperative useAnimatedStyle+withTiming API (which is crash-safe) if wanted. */}
        <Animated.View key={mode.key} style={styles.stageInner}>
          <GlassSurface
            variant="card"
            radius={glassTokens.radius.xl}
            onPress={() => launch(mode)}
            accessibilityLabel={`${mode.title} — ${mode.cta}`}
            accessibilityRole="button"
            style={styles.entryCardHost}
            contentStyle={styles.entryCard}
          >
            <LinearGradient
              colors={colors.gradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.medallion}
            >
              <Ionicons name={mode.icon} size={44} color={colors.white} />
            </LinearGradient>
            <Text style={styles.entryTitle}>{mode.title}</Text>
            <Text style={styles.entryDesc}>{mode.desc}</Text>
            <View style={styles.cta}>
              <Text style={styles.ctaText}>{mode.cta}</Text>
              <Ionicons name="arrow-forward" size={16} color={colors.white} />
            </View>
          </GlassSurface>
        </Animated.View>
      </View>

      {/* ---- Bottom mode switcher: POST · STORY · ARTICLE ---- */}
      <View style={[styles.switcherHost, { paddingBottom: Math.max(insets.bottom, spacing.lg) + spacing.sm }]}>
        <GlassBar radius={glassTokens.radius.pill} contentStyle={styles.switcherContent}>
          <View
            style={styles.switcherTrack}
            onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
          >
            {segmentWidth > 0 && (
              <Animated.View
                pointerEvents="none"
                style={[styles.indicator, { width: segmentWidth }, indicatorStyle]}
              />
            )}
            {MODES.map((m, i) => {
              const active = i === modeIndex;
              return (
                <Pressable
                  key={m.key}
                  onPress={() => setModeIndex(i)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  accessibilityLabel={`${m.title} mode`}
                  style={styles.segment}
                >
                  <Text style={[styles.segmentLabel, active && styles.segmentLabelActive]}>
                    {m.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </GlassBar>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg, // opaque lavender: the hub is a full screen, not a veil
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
  },
  closeHost: {
    width: 44,
    height: 44,
  },
  closeContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeSpacer: {
    width: 44,
    height: 44,
  },
  title: {
    ...fonts.extrabold,
    fontSize: 20,
    color: colors.ink,
  },
  stage: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  stageInner: {
    alignItems: 'stretch',
  },
  entryCardHost: {
    // GlassSurface owns the glass; we only size/position.
  },
  entryCard: {
    alignItems: 'center',
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xl,
    gap: spacing.md,
  },
  medallion: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 14,
    elevation: 6,
  },
  entryTitle: {
    ...fonts.extrabold,
    fontSize: 24,
    color: colors.ink,
  },
  entryDesc: {
    ...fonts.regular,
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink2,
    textAlign: 'center',
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    minHeight: 44,
    paddingHorizontal: spacing.xxl,
    borderRadius: glassTokens.radius.pill,
    backgroundColor: colors.primary,
  },
  ctaText: {
    ...fonts.bold,
    fontSize: 15,
    color: colors.white,
  },
  switcherHost: {
    paddingHorizontal: spacing.xl,
  },
  switcherContent: {
    padding: spacing.xs,
  },
  switcherTrack: {
    flexDirection: 'row',
    position: 'relative',
  },
  indicator: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    borderRadius: glassTokens.radius.pill,
    backgroundColor: colors.primary,
  },
  // ≥44pt segments — comfortable hit targets across all three modes.
  segment: {
    flex: 1,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  segmentLabel: {
    ...fonts.bold,
    fontSize: 13,
    letterSpacing: 1.2,
    color: colors.ink2,
  },
  segmentLabelActive: {
    color: colors.white,
  },
});
